import * as React from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { ArrowRightIcon, ComponentPlaceholderIcon } from '@radix-ui/react-icons'
import OtpInput from 'react-otp-input'

import { BlurredButton } from '~/components/mkt/blurred-button'
import { LanternInput } from '~/components/mkt/lantern-input'
import { loginRoute } from '~/constants/routing'
import { LoginFormResponseContainer } from './login-form-response'
import { useSignIn, useSignUp } from '@clerk/nextjs'
import { getLoginHref } from './get-login-href'
import { SENDERS } from '@basehub/emails/src/_constants'
import { redirectToNextParam } from '../../../(mkt)/_index-redirector/server'
import { Button } from '~/sotano-kit/primitives/button'
import { clsx } from 'clsx'

export type EmailStep = 'disabled' | 'input' | 'sending' | 'sent'

export const EmailLoginProvider = ({
  onNotAllowed,
  emailStep,
  setEmailStep,
}: {
  onNotAllowed: () => void
  emailStep: EmailStep
  setEmailStep: (step: EmailStep) => void
}) => {
  const [email, setEmail] = React.useState('')
  const router = useRouter()
  const { isLoaded, signIn, setActive } = useSignIn()
  const { isLoaded: isSignUpLoaded, signUp } = useSignUp()

  const searchParams = useSearchParams()
  const emailFromQuery = searchParams?.get('email')
  const nextQuery = searchParams?.get('next') ?? '/dashboard'

  const [action, setAction] = React.useState<'sign-in' | 'sign-up'>()

  const [sniperURL, setSniperURL] = React.useState<string | null>(null)

  const handleSubmitWithEmail = React.useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      if (emailStep === 'sending') return
      if (!isLoaded || !isSignUpLoaded) return
      setEmailStep('sending')
      try {
        setSniperURL(null)
        getSniperLink(email, SENDERS.login)
          .then((res) => {
            if ('url' in res) {
              setSniperURL(res.url)
            }
          })
          .catch((e) => {
            console.error(e)
          })

        const signInRes = await signIn.create({
          strategy: 'email_code',
          identifier: email,
        })
        if (signInRes.status === 'needs_first_factor') {
          // we're good, continue
          setTimeout(() => {
            setEmailStep('sent')
          }, 200)
          setAction('sign-in')
        }
      } catch (error) {
        // is new account probably
        try {
          const signUpRes = await signUp.create({ emailAddress: email })
          await signUpRes.prepareEmailAddressVerification({
            strategy: 'email_code',
          })
          setTimeout(() => {
            setEmailStep('sent')
          }, 200)
          setAction('sign-up')
        } catch (error) {
          onNotAllowed()
          // timeout to avoid flickering
          setTimeout(() => {
            setEmailStep('disabled')
          }, 200)
        }
      }
    },
    [
      email,
      isLoaded,
      isSignUpLoaded,
      onNotAllowed,
      setEmailStep,
      signIn,
      signUp,
      emailStep,
    ]
  )

  const handleOTPSubmit: SubmitOTPFormHandler = React.useCallback(
    async (value: string) => {
      if (!isLoaded || !isSignUpLoaded) throw new Error('something not loaded')
      if (action === 'sign-up') {
        const signUpRes = await signUp.attemptEmailAddressVerification({
          code: value,
        })
        if (signUpRes.status === 'complete') {
          await setActive({ session: signUpRes.createdSessionId })
          router.push(redirectToNextParam(nextQuery, true) ?? '/dashboard')
          return 'success'
        }
      } else {
        const signInRes = await signIn.attemptFirstFactor({
          code: value,
          strategy: 'email_code',
        })
        if (signInRes.status === 'complete') {
          await setActive({ session: signInRes.createdSessionId })
          setTimeout(
            () =>
              router.push(redirectToNextParam(nextQuery, true) ?? '/dashboard'),
            200
          )
          return 'success'
        }
      }

      throw new Error('Invalid code')
    },
    [
      action,
      isLoaded,
      isSignUpLoaded,
      router,
      setActive,
      signIn,
      signUp,
      nextQuery,
    ]
  )

  React.useEffect(() => {
    if (!emailFromQuery) return
    setEmailStep('input')
    setEmail(emailFromQuery)
  }, [emailFromQuery, setEmailStep])

  return (
    <>
      <div className="flex h-[120px] w-full flex-col">
        {emailStep === 'disabled' && (
          <Button
            unstyled
            focusStyled
            aria-label="continue with email"
            onClick={() => {
              setEmailStep('input')
            }}
            className="m-0 py-3 px-6 text-sm font-medium text-dark-muted transition-colors duration-150 hover:text-dark-dim"
          >
            Continue with Email
          </Button>
        )}
        {emailStep !== 'disabled' && emailStep !== 'sent' && (
          <form className="w-full" onSubmit={handleSubmitWithEmail}>
            <LanternInput
              inputProps={{
                name: 'email',
                type: 'email',
                value: email,
                autoFocus: emailStep === 'input',
                required: true,
                placeholder: 'Enter your email address',
                onChange: (e) => {
                  const value = e.currentTarget.value
                  setEmail(value)
                },
              }}
              className="!h-11"
            />
            <BlurredButton
              className="mx-auto mt-8 !w-40"
              loading={emailStep === 'sending'}
              loadingText="Submitting…"
              disabled={!email}
            >
              <span>Log In</span> <ArrowRightIcon />
            </BlurredButton>
          </form>
        )}
      </div>

      {/* OTP input validation */}
      <LoginFormResponseContainer
        visible={emailStep === 'sent'}
        back={{
          text: 'Back to login',
          handler: () => {
            setEmailStep('disabled')
            router.push(getLoginHref(loginRoute))
          },
        }}
      >
        <div className="flex flex-col items-center px-3 pt-16 pb-12 sm:px-11 sm:pb-[72px]">
          <p className="mb-4 mt-0 text-lg font-medium">Check your email</p>
          <p className="my-0 text-center text-base text-dark-muted">
            We&apos;ve sent a one-time verification code. <br />
            Please{' '}
            <SniperLinkWrapper href={sniperURL}>
              check your inbox
            </SniperLinkWrapper>{' '}
            at
            <br />
            <span className="text-dark-dim">{email}</span>
          </p>
          <div className="relative flex flex-col items-center">
            <OTPForm
              onSubmit={handleOTPSubmit}
              reFocus={emailStep === 'sent'}
              className="mt-10 sm:mt-14"
            />
          </div>
        </div>
      </LoginFormResponseContainer>
    </>
  )
}

export type SubmitOTPFormHandler = (
  value: string
) => Promise<'success' | 'invalid'>

export const OTPForm = ({
  onSubmit,
  reFocus,
  className,
}: {
  onSubmit: SubmitOTPFormHandler
  reFocus: boolean
  className?: string
}) => {
  const [value, setValue] = React.useState('')
  const [submitStatus, setSubmitStatus] = React.useState<
    'loading' | 'success' | 'invalid' | 'idle'
  >('idle')
  const otpRef = React.useRef<OtpInput>(null)

  React.useEffect(() => {
    if (submitStatus !== 'idle') return
    // When the otp input is filled with 6 characters, this useEffect will run the handleValidateCode function.
    if (value.length === 6) {
      setSubmitStatus('loading')
      onSubmit(value)
        .then((newStatus) => {
          setSubmitStatus(newStatus)
        })
        .catch(() => {
          setSubmitStatus('invalid')
        })
    } else setSubmitStatus('idle')
  }, [onSubmit, submitStatus, value])

  // hack for autofocus
  React.useEffect(() => {
    if (!reFocus || !otpRef.current) return
    otpRef.current?.focusInput(1)
    setTimeout(() => {
      otpRef.current?.focusPrevInput()
    }, 100)
  }, [reFocus])

  return (
    <>
      <form
        className={clsx('flex items-center justify-center text-lg', className)}
      >
        <OtpInput
          value={value}
          ref={otpRef}
          containerStyle="flex gap-1.5 sm:gap-2"
          focusStyle={{
            border: '1px solid #FF6C00',
            outline: 'none',
          }}
          inputStyle="rounded-xl bg-dark-shade-hover border border-dark-border-solid h-[66px] sm:h-[72px] min-w-[44px] sm:min-w-[48px] light:bg-dark-shade"
          onChange={(value: string) => {
            setValue(value)
            setSubmitStatus('idle')
          }}
          numInputs={6}
          hasErrored={submitStatus === 'invalid'}
          errorStyle={{
            border: '1px solid #FF4949',
            outline: 'none',
          }}
        />
      </form>
      {submitStatus === 'success' && (
        <p className="absolute -bottom-9 my-0 text-sm text-dark-faint">
          Redirecting...
        </p>
      )}
      {submitStatus === 'invalid' && (
        <p className="absolute -bottom-9 my-0 text-sm text-dark-error">
          Invalid code
        </p>
      )}
      {submitStatus === 'loading' && (
        <ComponentPlaceholderIcon className="absolute -bottom-9 h-6 w-6 animate-loading text-dark-faint" />
      )}
    </>
  )
}

async function getSniperLink(email: string, sender: string) {
  return await fetch(
    `https://sniperl.ink/v1/render?recipient=${email}&sender=${sender}`,
    { cache: 'no-store' }
  ).then(
    async (r) =>
      (await r.json()) as
        | {
            url: string
            image: string
            provider_pretty: string
          }
        | {
            code: string
            detail: string
            metadata: {
              recipient: string
            }
          }
  )
}

const SniperLinkWrapper = ({
  href,
  children,
}: {
  href: string | null
  children?: React.ReactNode
}) => {
  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener"
        className="text-dark-muted hover:text-dark-dim border-b border-dark-faint hover:border-dark-muted"
      >
        {children}
      </a>
    )
  }
  return <>{children}</>
}
